/**
 * SmartTable Class
 *
 * @example
 * new SmartTable(
 * model: <SmartTableModel> // Smarttable model with the following props
 *    limitRows: {Number} // optional property to limit number of rows in table
 *    showLoadMore: {Boolean} [true], // set to false if you don't want to display `Load More` bar in limited rows tables
 *    showHeader: {Boolean} [true], // set to false to hide table header
 *    columns: {array}, // required. an array of all columns in the table
 *
 *    tableClassName: {array|string}, // additional CSS classes to add to the table element, e.g. ["foo", "bar"] or "foo"
 *
 *    title:  {string}, // optional. title to show above the table, e.g. "My Table"
 *
 *    pager: {boolean}, // optional. pass false to hide table page
 *    mode: {string},    // either `client` or `server` (default). Table in `client` mode can do sorting locally
 *    height: ["auto"] // max-height of the table, e.g. 100, "auto", "100px", etc.
 *
 * collection: <Backbone.Collection> // just any Backbone Collection with data
 * )
 *
 * Column definition
 * TODO: Make column as a Backbone Model with defaults
 * {
 *    name: "Download", // [ALL] Name of the column, i.e. header
 *    attribute: "download_uri", // [ALL] Attribute to use to get the data from
 *    cell: "link", // [ALL] Type of the cell (see common/components/table/cells for available types)
 *    cellClassName: "download-link", // [ALL] Add this CSS class to every cell in a column
 *    beforeRender: function(content, rowModelData, rowModel, column) { // [ALL] Function to run over content before rendering in a cell
 *        return content
 *    },
 *    cellOptions: {
 *        prefix: "", // [Number] prefix to formatted attribute value
 *        suffix: "" // [Number] suffix to formatted attribute value
 *    },
 *    width: 300, // [ALL] width of the column
 *    locked: [false], // [ALL] if the column is frozen to the right
 * }
 * You can also group columns under another column. Assumptions:
 *  1) only single-level grouping is supported for now
 *  2) Locked columns cannot be grouped
 *  3) Cannot mix grouped columns with not-grouped with an exception for locked columns
 */
import { captureBreadcrumb } from "extensions/sentry";
import * as DOMPurify from "dompurify";

import {
    PazienError
} from "common/helpers/errors";
import {
    getWalkableModelValue,
    hasScrollBar,
    getScrollbarWidth,
    downloadCSV
} from "common/utils";

import { View } from "extensions/marionette";
import $ from "jquery";
import moment from "moment";
import { _ } from "extensions/underscore";

import { SmartTableBody } from "./body";

import { ExpandedRowView } from "./expandedRow";
import { HeaderView } from "./header";
import { PagerView } from "./pager";

import _template from "./smartTable.jst";
import scrollableTable from "./scrollableTable.jst";
import { toCSV } from "./csvExporter";

import "./smartTable.scss";

const TABLE_BORDER_WIDTH = 1; // TODO: make dynamic value based on actual table
const ns = function(cid) {
    return `.locked${cid}`;
};

export const SmartTable = View.extend({
    getTemplate() {
        if (this.model.isScrollable()) {
            return scrollableTable;
        }
        return _template;
    },
    className: "pz-table",
    regions: {
        tablePager: ".table-pager",
        tableStaticHeader: ".smarttable-header-static thead",
        tableLockedHeader: ".smarttable-header-locked thead"
    },
    ui: {
        tableTitle: ".table-name",
        tableLeftGradient: ".left-gradient",
        tableRightGradient: ".right-gradient",
        tableHeaderContainer: ".smarttable-header",
        tableHeaderStaticContainer: ".smarttable-header-static",
        tableHeaderLockedContainer: ".smarttable-header-locked",
        tableStaticContainer: ".smarttable-content-static",
        tableStaticBody: ".smarttable-content-static tbody",
        tableStaticColgroup: ".static-columns colgroup",
        tableLockedContainer: ".smarttable-content-locked",
        tableLockedBody: ".smarttable-content-locked tbody",
        tableLockedColgroup: ".locked-columns colgroup",
        loadMore: ".load-more",
        loadAll: ".load-all",
        outerHorizontalScroll: ".horizontal-scrollbar",
        innerHorizontalScroll: ".inner-scrollbar"
    },
    
    events() {
        const events = {};
        
        if (this.model && this.model.get("showLoadMore") && this.model.get("limitRows")) {
            _.extend(events, {
                "click .more": "loadMoreRows",
                "click .all": "loadAllRows"
            });
        }
        
        if (this.getOption("onMouseenterCell")) {
            _.extend(events, {
                "mouseenter td": "_onMouseenterCell"
            });
        }
        
        if (this.getOption("onMouseleaveCell")) {
            _.extend(events, {
                "mouseleave td": "_onMouseleaveCell"
            });
        }
        return events;
    },
    
    initialize() {
        // eslint-disable-next-line eqeqeq
        if (this.collection == null) {
            throw new PazienError({
                message: "Collection is required for this view"
            });
        }
        
        // Subscribe to page change event from the pager to fetch more data
        this.listenTo(this.model.pagerModel, "change:page", function(pagerModel, page) {
            this.collection.getPage(page);
        });
        
        // Subscribe to limit change event from pager
        this.listenTo(this.model.pagerModel, "change:limit", function(pagerModel, limit) {
            this.collection.setLimit(limit);
        });
        
        this.listenTo(this.collection, "change:state", function(collection, state) {
            this.model.pagerModel.set({
                page: state.page,
                total: state.count,
                limit: state.limit
            });
        });
        
        this.listenTo(this.model, "change:limitRows change:filterRows", function() {
            this.body.render();
            this.body.checkEmptyView();
            this.checkVisibilityOfLoadMoreButtons();
            this.setHeaderPadding();
            this.reflowFixedTable();
        });
    },
    
    getDefaultFilterParams() {
        return this.model.columns.reduce((memo, model) => {
            if (model.get("filterable")) {
                const active = model.get("filterOptions").filter((el) => el.active === true);
                if (active.length > 1) {
                    throw new Error("Only one active item per filter is allowed", model);
                }
                
                if (active.length === 1) {
                    memo[model.get("attribute")] = active[0].value;
                }
            }
            return memo;
        }, {});
    },
    
    clientSort(cellType, cellAttribute, order, cellOptions) {
        this.collection.comparator = function clientSortSmartTableComparator(a, b) {
            let valA = getWalkableModelValue(a, cellAttribute);
            let valB = getWalkableModelValue(b, cellAttribute);
            
            const transformEach = function(transformer) {
                valA = transformer(valA);
                valB = transformer(valB);
            };
            
            if (typeof cellType === "function") {
                cellType = cellType();
            }
            
            // Normalize value based on cell type
            switch (cellType) {
            case "number": {
                transformEach((val) => {
                    if (Number.isNaN(val)) {
                        return 0;
                    }
                    return val;
                });
            
                break;
            }
            case "date_time": {
                transformEach((val) => moment.utc(val).valueOf());
            
                break;
            }
            case "rate": {
                transformEach((val) => {
                    if (typeof val === "undefined" || Number.isNaN(val)) {
                        return 0;
                    }
                    return Object.prototype.hasOwnProperty.call(val, "float") ? val.float : val;
                });
            
                break;
            }
            case "text": {
                transformEach((val) => {
                    if (_.isString(val)) {
                        return val.toLowerCase();
                    } if (_.isObject(val)) {
                        let sortByValue;
                        if (cellOptions && cellOptions.sortBy && _.has(val, cellOptions.sortBy)) {
                            sortByValue = val[cellOptions.sortBy];
                        } else {
                            // Order of keys is not guaranteed in an object. Use sortBy under cellOptions
                            sortByValue = _.chain(val).values().first().value();
                        }
                        
                        if (_.isNumber(sortByValue.float)) {
                            return sortByValue.float;
                        }
                        return sortByValue;
                    }
                    
                    return val;
                });
            
                break;
            }
            default: { break; }
            }
            
            // Return value based on order
            return ((valA > valB) || (valA === valB) - 1) * Number.parseInt(order, 10);
        };
        
        this.collection.sort();
    },
    
    showExpandedRow(opts) {
        const { after, content } = opts;
        
        if (after.expandedRow) {
            after.$el.removeClass("expanded");
            after.expandedRow.hide();
            delete after.expandedRow;
            return;
        }
        
        after.expandedRow = new ExpandedRowView({
            content,
            colspan: after.columns.length
        });
        after.$el.addClass("expanded");
        
        after.expandedRow.show().$el.insertAfter(after.$el);
    },
    
    syncStaticCellHeightWithLockedCell() {
        if (this.model.hasLockedColumns()) {
            const staticTableRows = this.ui.tableStaticBody.find("tr");
            this.ui.tableLockedBody.find("tr").each(function(index) {
                $(this).find("td").css("height", staticTableRows.eq(index).height());
            });
        }
    },
    
    onBeforeDestroy() {
        if (this.expandedRow) {
            this.expandedRow.destroy();
        }
        
        _.each(["body", "bodyLocked"], function(instanceName) {
            if (this[instanceName]) {
                // Prevent memory leaks from table"s body event handlers
                this[instanceName].destroy();
            }
        }, this);
        
        // Unbind events
        if (this.isRendered()) {
            this.ui.tableStaticContainer.off(ns(this.cid));
            this.ui.tableLockedContainer.off(ns(this.cid));
        }
        $(window).off(ns(this.cid));
    },
    
    /**
     * Never trust the API to return sorted list in `client` mode...
     */
    sortCollection() {
        // Return earlier if table mode is NOT `client`
        if (this.model.get("mode") !== "client") {
            return;
        }
        
        // Try to get active sort property
        const initialSortProp = this.model.columns.find((model) => ["1", "-1"].includes(model.get("sortOptions").active));
        
        if (initialSortProp) {
            const sortOptions = initialSortProp.get("sortOptions");
            
            this.clientSort(
                initialSortProp.get("cell"),
                sortOptions.attribute || initialSortProp.get("attribute"),
                sortOptions.active,
                initialSortProp.get("cellOptions")
            );
        }
    },
    
    checkVisibilityOfLoadMoreButtons() {
        const currentLimit = this.model.get("limitRows");
        
        this.$(".limitRows").text(Math.min(currentLimit, this.collection.length));
        
        if (!currentLimit || currentLimit >= this.collection.length) {
            this.ui.loadMore.hide();
            this.ui.loadAll.hide();
        }
    },
    
    showRows(limit) {
        this.model.set("limitRows", limit);
        this.checkVisibilityOfLoadMoreButtons();
    },
    
    loadMoreRows(e) {
        e.preventDefault();
        this.showRows(Math.min(this.model.get("limitRows") * 2, this.collection.length));
    },
    
    loadAllRows(e) {
        e.preventDefault();
        this.showRows(this.collection.length);
    },
    
    filterBy(obj) {
        this.collection.filterBy(obj);
    },
    
    /**
     *  Add header to the table
     */
    addTableHeader(opts) {
        const options = _.defaults({}, opts, {
            locked: false
        });
        const _region = this.getRegion(options.region);
        
        let primaryColumnsCollection;
        let secondaryColumnsCollection;
        
        if (!_region) {
            throw new Error(`Invalid region name: ${_region}`);
        }
        
        if (options.locked) {
            primaryColumnsCollection = this.model.lockedColumns;
            secondaryColumnsCollection = this.model.staticColumns;
        } else {
            primaryColumnsCollection = this.model.staticColumns;
            secondaryColumnsCollection = this.model.lockedColumns;
        }
        
        const header = new HeaderView({
            collection: primaryColumnsCollection,
            secondaryCollection: secondaryColumnsCollection
        });
        
        header.on("childview:filter", (headerView, cell, filterBy) => {
            const obj = {};
            obj[cell] = filterBy;
            
            this.filterBy(obj);
        }, this);
        
        header.on("childview:select", function(childView, items) {
            if (items === "all") {
                this.collection.each((model) => {
                    model.set("selected", true);
                });
            } else if (items === "none") {
                this.collection.each((model) => {
                    model.set("selected", false);
                });
            }
        }, this);
        
        header.on("childview:click", (view, attr, event) => {
            this._onHeaderClick(view, attr, event);
        });
        
        header.on("childview:sort", function(headerView, cell, order, cellType) {
            const tableMode = this.model.get("mode");
            const doClientSort = () => {
                const collection = this.collection;
                let cellAttribute = cell;
                const sortAttr = headerView.model.get("sortOptions").attribute;
                // Check if cell config has a separate sort attribute
                if (sortAttr && collection.some((m) => m.has(sortAttr))) {
                    cellAttribute = sortAttr;
                }
                this.clientSort(cellType, cellAttribute, order, headerView.model.get("cellOptions"));
            };
            
            if (tableMode === "server") {
                const sortOptions = headerView.model.get("sortOptions");
                const sortAttribute = sortOptions.attribute || cell;
                
                if (_.isString(sortAttribute)) {
                    this.collection.apiSort({
                        sort: sortAttribute,
                        order
                    });
                } else {
                    doClientSort();
                }
            } else if (tableMode === "client") {
                doClientSort();
            }
            this.syncStaticCellHeightWithLockedCell();
        }, this);
        
        _region.show(header);
        
        if (this.model.hasGroupingColumns()) {
            if (options.locked) {
                // Make column headers taller
                header.children.each((child) => {
                    child.$el.addClass("doubleHeight");
                });
            } else {
                const groupingRow = new HeaderView({
                    collection: this.model.groupColumns
                });
                groupingRow.render();
                _region.$el.prepend(groupingRow.$el);
            }
        }
    },
    
    // Add table to the table region
    addTableBody(opts) {
        const options = _.defaults({}, opts, {
            locked: false
        });
        const instanceName = options.locked ? "bodyLocked" : "body";
        
        this[instanceName] = new SmartTableBody({
            el: options.container[0],
            model: this.model,
            collection: this.collection,
            locked: options.locked
        });
        
        this.listenTo(this[instanceName], "childview:click:row", (childView) => {
            const onClickRow = this.getOption("onClickRow");
            if (_.isFunction(onClickRow)) {
                onClickRow.call(this, childView, childView.model.id);
            }
        });
        
        this.listenTo(this[instanceName], "childview:click:cell", this._onClickCell);
        
        // Proxy events to the instance
        this.listenTo(this[instanceName], "render:empty", () => { this.showingEmptyView = true; });
        this.listenTo(this[instanceName], "remove:empty", () => { this.showingEmptyView = false; });
        
        this.listenTo(this[instanceName], "render:children", () => {
            if (!this.attached) { return; }
            this.trigger("render:children");
            if (options.locked) {
                this.syncStaticCellHeightWithLockedCell();
                this.setLockedStaticWidth();
            } else {
                this.setHeaderPadding();
                this.reflowFixedTable();
            }
        });
        
        this[instanceName].render();
    },
    
    reflowFixedTable() {
        if ((this.model.isScrollable() || this.model.growFirstColumn()) && !this.showingEmptyView) {
            this.toggleRightGradientForFixedTable();
            window.requestAnimationFrame(() => {
                if (this.isDestroyed()) { return; }
                // Set width: auto to the header
                this.ui.tableHeaderStaticContainer.find("table").css("width", "auto");
                this.ui.tableStaticContainer.find("table").css("width", "auto");
                
                // Sync table content with header
                const THs = this.ui.tableHeaderStaticContainer.find("tr:first").children().toArray();
                
                // Hold values in this array
                const min_width = [];
                const allRows = this.ui.tableStaticBody.find("tr");
                
                allRows.first().children().toArray().forEach((td, i) => {
                    min_width[i] = Math.max(THs[i].getBoundingClientRect().width, td.getBoundingClientRect().width, 42);
                });
                
                const totalWidth = this.el.getBoundingClientRect().width - 2; // 2 is border
                // If we grow first column in this table we should subtract from the first column all extra space created
                if (this.model.growFirstColumn()) {
                    min_width[0] = totalWidth - min_width.reduce((memo, acc, i) => {
                        if (i > 0) {
                            return memo + acc;
                        }
                        return memo;
                    }, 0);
                }
                
                window.requestAnimationFrame(() => {
                    if (this.isDestroyed()) {
                        return;
                    }
                    allRows.each((idx, tr) => {
                        this.$(tr).children().each((i, td) => {
                            td.style.minWidth = `${min_width[i]}px`;
                        });
                    });
                    min_width.forEach((val, index) => {
                        THs[index].style.minWidth = `${min_width[index]}px`;
                    });
                    
                    // Set back width 100%
                    this.ui.tableHeaderStaticContainer.find("table").css("width", "100%");
                    this.ui.tableStaticContainer.find("table").css("width", "100%");
                    
                    // Place horizontal scrollbar
                    // Read current width if the table
                    this.ui.innerHorizontalScroll.width(this.ui.tableStaticBody.width());
                });
            });
        }
    },
    
    toggleRightGradientForFixedTable() {
        this.ui.tableRightGradient.toggleClass("off", !hasScrollBar(this.ui.tableStaticContainer).horizontal);
    },
    
    /**
     *  Set padding to table header to compensate for a scrollbar
     */
    setHeaderPadding() {
        if (!this.attached) { return; }
        if (this.model.isScrollable()) { return; }
        const self = this;
        window.setTimeout(() => {
            if (self.ui.tableHeaderContainer.css) {
                self.ui.tableHeaderContainer.css("padding-right", () => {
                    const containerEl = self.ui.tableStaticContainer[0];
                    return containerEl.offsetWidth - containerEl.clientWidth;
                });
            }
        }, 100);
    },
    
    /**
     * Add paginator if enabled
     */
    addPaginator() {
        const hasPager = this.model.get("pager");
        
        if (this.getRegion("tablePager").$el) {
            this.getRegion("tablePager").$el.toggleClass("hide", !hasPager);
        }
        
        if (hasPager) {
            if (this.collection.state) {
                this.model.pagerModel.set({
                    page: this.collection.state.get("page"),
                    total: this.collection.state.get("count"),
                    limit: this.collection.state.get("limit")
                });
            }
            const pager = new PagerView({
                model: this.model.pagerModel
            });
            
            // Return earlier if table has a
            if (this.getOption("skipPagerView")) return;
            
            this.showChildView("tablePager", pager);
        }
    },
    
    /**
     * Add table title if provided
     */
    addTitle() {
        if (this.model.has("title")) {
            this.ui.tableTitle.text(this.model.get("title"));
        } else {
            this.ui.tableTitle.addClass("hidden");
        }
    },
    
    /**
     * Add optional css class name if provided
     */
    addClassName() {
        if (this.model.has("tableClassName")) {
            this.$el.addClass(this.model.get("tableClassName"));
        }
        
        if (this.model.get("showHeader") === false) {
            this.$el.addClass("no-header");
        }
        
        if (this.model.isScrollable()) {
            this.$el.addClass("scrollable");
        }
    
        if (this.model.growFirstColumn()) {
            this.$el.addClass("grow-td1");
        }
        
        if (this.model.get("pager")) {
            this.$el.addClass("has-pager");
        }
    },
    
    /**
     * Render table colgroup
     * @param {Object} opts options
     * @param {jQuery.Element} opts.container element where to insert colgroups
     * @param {Boolean} opts.locked whether or not render for locked column
     */
    configureColgroup(opts) {
        const _opts = _.defaults({}, opts, {
            locked: false
        });
        
        const collection = _opts.locked ? this.model.lockedColumns : this.model.staticColumns;
        
        const $cols = collection.reduce((memo, column) => {
            const attr = column.get("attribute");
            const $col = $("<col/>", {
                "data-name": _.isFunction(attr) ? "dynamic" : attr,
                "class": column.get("cellClassName")
            });
            const width = column.get("width");
            
            // eslint-disable-next-line eqeqeq
            if (width != null) {
                $col.css("width", width);
            }
            
            return memo.append($col);
        }, $("<colgroup/>"));
        
        _opts.container.append(DOMPurify.sanitize($cols.html()));
    },
    
    displayTableContent() {
        this.configureColgroup({
            container: this.ui.tableStaticColgroup,
            locked: false
        });
        if (this.model.get("showHeader")) {
            this.addTableHeader({
                region: "tableStaticHeader",
                locked: false
            });
        }
        this.addTableBody({
            container: this.ui.tableStaticBody,
            locked: false
        });
    },
    
    renderLockedColumns() {
        this.configureColgroup({
            container: this.ui.tableLockedColgroup,
            locked: true
        });
        
        if (this.model.get("showHeader")) {
            this.addTableHeader({
                region: "tableLockedHeader",
                locked: true
            });
        }
        
        this.addTableBody({
            container: this.ui.tableLockedBody,
            locked: true
        });
    },
    
    templateContext() {
        return {
            showGradient: this.model.hasLockedColumns(),
            hasLockedColumns: this.model.hasLockedColumns(),
            totalRows: this.collection.length
        };
    },
    
    _getLockedColumnsWidth() {
        return this.model.lockedColumns.getTotalWidth() + TABLE_BORDER_WIDTH * 2;
    },
    
    setLockedStaticWidth() {
        const self = this;
        
        if (this.isDestroyed() || !this.attached) { return; }
        
        if (this.model.hasLockedColumns()) {
            // Group all "reads" together so they can be called in a single frame
            const TOTAL_ELEMENT_WIDTH = this.$el.width() || 800;
            const staticTableWidth = this.model.staticColumns.getTotalWidth();
            let lockedColumnsWidth = this._getLockedColumnsWidth();
            let staticTableContainerWidth = TOTAL_ELEMENT_WIDTH - lockedColumnsWidth;
            let hasScrollbar;
            
            if (staticTableWidth < staticTableContainerWidth) {
                staticTableContainerWidth = staticTableWidth;
                lockedColumnsWidth = TOTAL_ELEMENT_WIDTH - staticTableContainerWidth;
                hasScrollbar = hasScrollBar(this.ui.tableStaticContainer).vertical;
            }
            
            this.ui.tableHeaderLockedContainer.css({
                width: lockedColumnsWidth
            });
            
            this.ui.tableLockedContainer.css({
                width: lockedColumnsWidth
            });
            
            this.ui.tableLeftGradient.css({
                left: lockedColumnsWidth
            });
            
            this.ui.tableStaticContainer.css({
                width: staticTableContainerWidth,
                "max-height": this.model.get("height")
            });
            
            // There is no mistake here and don't move this code higher up. We need to make sure it runs in `write` frame and doesn't trigger
            // another reflow
            if (staticTableWidth < staticTableContainerWidth) {
                this.setStaticTableWidth(staticTableContainerWidth, hasScrollbar ? 25 : TABLE_BORDER_WIDTH);
            }
            
            this.ui.tableHeaderStaticContainer.css({
                width() {
                    const scrollbarWidth = self.ui.tableStaticContainer[0].offsetWidth - self.ui.tableStaticContainer[0].clientWidth - TABLE_BORDER_WIDTH * 2;
                    return staticTableContainerWidth - scrollbarWidth;
                }
            });
            
            this.ui.tableRightGradient.css({
                left: lockedColumnsWidth + staticTableContainerWidth - (hasScrollbar ? 25 : TABLE_BORDER_WIDTH) - 25
            });
            
            if (this.model.get("scrollRight")) {
                this.ui.tableStaticContainer.scrollLeft(staticTableWidth);
            }
        }
    },
    
    setStaticTableWidth(width, offset = 0) {
        this.ui.tableStaticContainer.find("table").css({
            width: Math.floor(width - offset),
            minWidth: Math.floor(width - offset)
        });
        
        this.ui.tableHeaderStaticContainer.find("table").css({
            width: Math.floor(width - offset),
            minWidth: Math.floor(width - offset)
        });
    },
    
    setCSSDimensions() {
        if (this.isDestroyed() || !this.attached) { return; }
        
        if (this.model.hasLockedColumns()) {
            const staticColumnsWidth = this.model.staticColumns.getTotalWidth();
            const staticTableHasHorizontalScrollbar = hasScrollBar(this.ui.tableStaticContainer).horizontal;
            const modelHeight = this.model.get("height");
            let lockedPadding = 0;
            
            if (staticTableHasHorizontalScrollbar) {
                lockedPadding = getScrollbarWidth;
            }
            
            this.ui.tableLockedContainer.css({
                "max-height": modelHeight,
                "padding-bottom": lockedPadding
            });
            
            if (!staticTableHasHorizontalScrollbar) {
                this.ui.tableLockedContainer.css({
                    "border-bottom": 0
                });
            }
            
            this.ui.tableStaticContainer.css({
                "max-height": modelHeight
            });
            
            this.setStaticTableWidth(staticColumnsWidth);
            this.setLockedStaticWidth();
        } else {
            // Set height to the content container
            this.ui.tableStaticContainer.css({
                "max-height": this.model.get("height")
            });
        }
    },
    
    bindEventsForScrollableTable() {
        const self = this;
        const staticContentTable = this.ui.tableStaticBody;
        const staticContent = this.ui.tableStaticContainer;
        const staticHeader = this.ui.tableHeaderStaticContainer;
        const horizontalScroll = this.ui.outerHorizontalScroll;
        
        const leftGradient = this.ui.tableLeftGradient;
        const rightGradient = this.ui.tableRightGradient;
        
        const checkGradiendVisibility = (scrollLeft) => {
            const staticWidth = staticContentTable[0].offsetWidth - staticContent[0].offsetWidth;
            
            leftGradient.toggleClass("off", scrollLeft < 5);
            rightGradient.toggleClass("off", staticWidth - scrollLeft < 1);
        };
        
        let supportsPositionsSticky;
        
        try {
            supportsPositionsSticky = CSS.supports("position", "sticky");
        } catch {
            supportsPositionsSticky = false;
            captureBreadcrumb("Browser does not support 'CSS.supports' feature", {
                level: "warning"
            });
        }
        
        if (supportsPositionsSticky) {
            this.$el.addClass("supports-sticky");
            horizontalScroll.off(`scroll${ns(this.cid)}`).on(`scroll${ns(this.cid)}`, function() {
                window.requestAnimationFrame(() => {
                    if (self.isDestroyed()) {
                        return;
                    }
                    // Store scrollLeft in a variable to avoid reading it twice causing a reflow
                    const scrollLeft = this.scrollLeft;
                    staticHeader[0].scrollLeft = scrollLeft;
                    staticContent[0].scrollLeft = scrollLeft;
                    checkGradiendVisibility(scrollLeft);
                });
            });
            
            staticContent.off(`mousewheel${ns(this.cid)}`).on(`mousewheel${ns(this.cid)}`, (event) => {
                window.requestAnimationFrame(() => {
                    if (this.isDestroyed()) {
                        return;
                    }
                    let delta = event.originalEvent.wheelDeltaX * 0.8;
                    
                    // Support "natural" scroll hipsters at least in Safari
                    if (event.webkitDirectionInvertedFromDevice) {
                        delta = -delta;
                    }
                    const scrollLeft = staticContent[0].scrollLeft - delta;
                    
                    staticContent[0].scrollLeft = scrollLeft;
                    staticHeader[0].scrollLeft = scrollLeft;
                    horizontalScroll[0].scrollLeft = scrollLeft;
                    
                    checkGradiendVisibility(scrollLeft);
                });
            });
        } else {
            staticContent.off(`scroll${ns(this.cid)}`).on(`scroll${ns(this.cid)}`, function() {
                window.requestAnimationFrame(() => {
                    if (self.isDestroyed()) { return; }
                    const scrollLeft = this.scrollLeft;
                    staticHeader[0].scrollLeft = scrollLeft;
                    checkGradiendVisibility(scrollLeft);
                });
            });
        }
        
        const events = `resize${ns(this.cid)} shown.bs.tab${ns(this.cid)} sidebar:updated${ns(this.cid)}`;
        $(window).off(events).on(events, () => {
            window.requestAnimationFrame(() => {
                if (this.isDestroyed()) { return; }
                this.toggleRightGradientForFixedTable();
            });
        });
    },
    
    bindEventsForTableWithLockedColumn() {
        // Bind simultaneous scroll events
        const self = this;
        const lockedContent = this.ui.tableLockedContainer;
        const staticContent = this.ui.tableStaticContainer;
        const staticContentTable = this.ui.tableStaticBody;
        const staticHeader = this.ui.tableHeaderStaticContainer;
        const leftGradient = this.ui.tableLeftGradient;
        const rightGradient = this.ui.tableRightGradient;
        
        staticContent.off(`scroll${ns(this.cid)}`).on(`scroll${ns(this.cid)}`, function() {
            window.requestAnimationFrame(() => {
                if (self.isDestroyed()) { return; }
                const { scrollLeft, scrollTop } = this;
                const staticWidth = staticContentTable[0].offsetWidth - staticContent[0].offsetWidth;
                staticHeader[0].scrollLeft = scrollLeft;
                lockedContent[0].scrollTop = scrollTop;
                
                leftGradient.toggleClass("off", scrollLeft < 26);
                rightGradient.toggleClass("off", (staticWidth - scrollLeft) < 26);
            });
        });
        
        lockedContent.off(`mousewheel${ns(this.cid)}`).on(`mousewheel${ns(this.cid)}`, (event) => {
            window.requestAnimationFrame(() => {
                if (this.isDestroyed()) { return; }
                let delta = event.originalEvent.wheelDeltaY;
                
                // Support "natural" scroll hipsters at least in Safari
                if (event.webkitDirectionInvertedFromDevice) {
                    delta = -delta;
                }
                staticContent[0].scrollTop = staticContent[0].scrollTop - delta;
            });
            
            // Don't always prevent default event (causes paralax scroll) but check if we reached staticContent top/bottom and then allow default action
            const isBottom = staticContent[0].scrollHeight < staticContent.scrollTop() + staticContent.innerHeight() + 16;
            const isTop = staticContent.scrollTop() === 0;
            if (!isBottom && !isTop) {
                event.preventDefault();
            }
        });
        
        // Resize table on window resize
        $(window).off(`resize${ns(this.cid)}`).on(`resize${ns(this.cid)}`, _.bind(this.setLockedStaticWidth, this));
        $(window).off(`shown.bs.tab${ns(this.cid)}`).on(`shown.bs.tab${ns(this.cid)}`, _.bind(function() {
            this.syncStaticCellHeightWithLockedCell();
            this.setLockedStaticWidth();
        }, this));
    },
    
    onBeforeRender() {
        this.sortCollection();
    },
    
    onRender() {
        const hasLockedColumns = this.model.hasLockedColumns();
        
        this.displayTableContent();
        
        if (hasLockedColumns) {
            this.renderLockedColumns();
            this.bindEventsForTableWithLockedColumn();
        }
        
        if (this.model.isScrollable()) {
            this.bindEventsForScrollableTable();
        }
        
        this.$el.toggleClass("table-hasLockedColumns", hasLockedColumns);
        
        this.addPaginator();
        this.addTitle();
        this.addClassName();
    },
    
    onAttach() {
        this.attached = true;
        this.setCSSDimensions();
        this.setHeaderPadding();
        this.syncStaticCellHeightWithLockedCell();
        this.reflowFixedTable();
    },
    
    _onHeaderClick(view, attr, event) {
        const onClickHeader = this.getOption("onClickHeader");
        if (typeof onClickHeader === "function") {
            onClickHeader.call(this, view, attr, event);
        }
    },
    
    _onClickCell(rowView, cellAttribute, rowModel, cell, jqEvent) {
        // If checkbox cell was clicked then flip `selected` property of a row model
        if (cellAttribute === "selected") {
            rowModel.set("selected", !rowModel.get("selected"));
        } else {
            const onClickCell = this.getOption("onClickCell");
            if (_.isFunction(onClickCell)) {
                onClickCell.call(this, rowView, cellAttribute, rowModel, cell, jqEvent);
            }
        }
    },
    
    _getExtraDataByEventFromCell(e) {
        const $cell = $(e.currentTarget);
        
        // TODO: think about better approach to find if cell is in locked-columns table
        const isCellInLockedTable = $cell.closest("table").parent().hasClass("smarttable-content-locked");
        
        return {
            row: this.collection.at($(e.currentTarget).closest("tr").index()),
            column: this.model[isCellInLockedTable ? "lockedColumns" : "staticColumns"].at($cell.index())
        };
    },
    
    _onMouseenterCell(e) {
        const onMouseenterCell = this.getOption("onMouseenterCell");
        if (_.isFunction(onMouseenterCell)) {
            onMouseenterCell.call(this, e, this._getExtraDataByEventFromCell(e));
        }
    },
    
    _onMouseleaveCell(e) {
        const onMouseleaveCell = this.getOption("onMouseleaveCell");
        if (_.isFunction(onMouseleaveCell)) {
            onMouseleaveCell.call(this, e, this._getExtraDataByEventFromCell(e));
        }
    },
    
    // Subclasses or instances should implement these methods to handle click events
    onClickRow(rowView, rowModelID) {},
    onClickCell(rowView, cellAttribute, rowModel, cell, jqEvent) {},
    
    toCSV(filename) {
        downloadCSV(filename, toCSV(this));
    }
});
